import { create } from 'zustand';

export type ResultRequiredAttachedFile = {
  id: string;
  name: string;
  file?: File;
};

type ResultRequiredModalPayload = {
  taskId?: string | number;
  title?: string;
};

type ResultRequiredModalState = {
  isOpen: boolean;
  payload: ResultRequiredModalPayload | null;

  resultText: string;
  files: ResultRequiredAttachedFile[];

  afterSaveAction: (() => void) | null;

  open: (payload?: ResultRequiredModalPayload) => void;
  close: () => void;

  setResultText: (text: string) => void;

  addFiles: (picked: File[]) => void;
  removeFile: (id: string) => void;

  setAfterSaveAction: (fn: (() => void) | null) => void;
  runAfterSaveAction: () => Promise<void>;

  clearForm: () => void;
  resetAll: () => void;
};

function uid() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

export const useResultRequiredModalStore = create<ResultRequiredModalState>(
  (set, get) => ({
    isOpen: false,
    payload: null,

    resultText: '',
    files: [],

    afterSaveAction: null,

    open: (payload) =>
      set({
        isOpen: true,
        payload: payload ?? null,
      }),

    close: () => set({ isOpen: false }),

    setResultText: (text) => set({ resultText: text }),

    addFiles: (picked) =>
      set((s) => ({
        files: [
          ...s.files,
          ...picked.map((f) => ({
            id: uid(),
            name: f.name,
            file: f,
          })),
        ],
      })),

    removeFile: (id) =>
      set((s) => ({
        files: s.files.filter((f) => f.id !== id),
      })),

    setAfterSaveAction: (fn) => set({ afterSaveAction: fn }),

    runAfterSaveAction: async () => {
      const fn = get().afterSaveAction;
      set({ afterSaveAction: null });

      try {
        await fn?.();
      } catch (e) {
        console.error('afterSaveAction error', e);
      }
    },

    clearForm: () =>
      set({
        resultText: '',
        files: [],
        payload: null,
        afterSaveAction: null,
      }),

    resetAll: () =>
      set({
        isOpen: false,
        payload: null,
        resultText: '',
        files: [],
        afterSaveAction: null,
      }),
  })
);
